import { FAKE_WIDGET_ID } from "@services/widgets/utils";
import React, { useContext, useState, useLayoutEffect } from "react";
import { WidgetContext } from "./index";

export const MAX_ITEMS = 3;
export const MAX_PAGES = 6;
export const getNewPage = (): Pick<
  UserWidgetScreen,
  "label" | "userWidgetScreenId" | "widgets"
> => ({
  label: "",
  userWidgetScreenId: FAKE_WIDGET_ID,
  widgets: [],
});

export const computePages = (pages: UserWidgetScreen[]): UserWidgetScreen[] => {
  const page = pages.findIndex((page) => page.widgets.length > MAX_ITEMS);
  if (page < 0) return pages;

  if (page === pages.length - 1) pages.push(getNewPage());
  const item = pages[page].widgets[MAX_ITEMS];
  pages[page].widgets.splice(MAX_ITEMS, 1);
  pages[page + 1].widgets.splice(0, 0, item);

  return computePages(pages);
};

export const removeWidget = (
  widget: Pick<UserWidget, "userWidgetId">,
  { pages }: Pick<WidgetContextEntity, "pages">
): WidgetContextEntity["pages"] => {
  return [
    ...pages.map((page) => {
      return {
        ...page,
        widgets: page.widgets.filter(
          (w) => w.userWidgetId !== widget.userWidgetId
        ),
      };
    }),
  ];
};

export const useIsCurrentPage = (widget?: UserWidget) => {
  const { pages, currentPage } = useContext(WidgetContext);
  if (typeof widget === "undefined") return false;
  const [pageNumber] = getPageIndex(pages, widget);
  return pageNumber === currentPage;
};
export const usePositionAnimation = (
  ref: React.RefObject<HTMLDivElement | null>,
  widget?: UserWidget,
  animationLength?: number
) => {
  const computePos = (pages: UserWidgetScreen[]) =>
    pages
      .map((p) => [...p.widgets])
      .flat()
      .findIndex((w) => w.userWidgetId === widget?.userWidgetId);
  const { pages, previousPages, isDragging } = useContext(WidgetContext);
  const [{ previousPos, position }, setPos] = useState<{
    previousPos: number;
    position: number;
  }>({
    previousPos: computePos(pages),
    position: computePos(pages),
  });

  useLayoutEffect(() => {
    const position = computePos(pages);
    const previousPos = computePos(previousPages);
    if (position !== previousPos) {
      const domNode = ref.current;
      if (domNode === null) return;
      if (widget?.userWidgetId === isDragging) return;
      requestAnimationFrame(() => {
        // Before the DOM paints, invert child to old position
        domNode.style.opacity = "0";
        domNode.style.transition = "opacity 0s";
        requestAnimationFrame(() => {
          // After the previous frame, remove
          // the transistion to play the animation
          domNode.style.opacity = "1";
          domNode.style.transition = `opacity ${animationLength ?? 300}ms ease`;
        });
      });
    }
    setPos({
      previousPos: computePos(previousPages),
      position: computePos(pages),
    });
  }, [pages]);

  return { previousPos, position };
};
export const getNewWidgetPosition = ({
  pages,
  currentPage,
}: Pick<WidgetContextEntity, "pages" | "currentPage">) => {
  return pages[currentPage].widgets.length >= MAX_ITEMS
    ? pages.length - 1
    : currentPage;
};

export const getNewWidgetPages = (
  { pages, currentPage }: Pick<WidgetContextEntity, "pages" | "currentPage">,
  widget: UserWidgetCreation
) => {
  const position = getNewWidgetPosition({ pages, currentPage });
  return computePages([
    ...pages.map((page, index) => {
      if (position !== index) return page;
      return {
        ...page,
        widgets: [
          ...page.widgets,
          {
            ...widget,
            userWidgetScreenId: page.userWidgetScreenId,
            userWidgetId: FAKE_WIDGET_ID,
          },
        ],
      };
    }),
  ]);
};

export const getPageIndex = (
  pages: Pick<UserWidgetScreen, "widgets">[],
  widget: Pick<UserWidget, "userWidgetId">
): [pageNumber: number, index: number] => {
  let index = -1;
  const pageNumber = pages.findIndex((page) => {
    const temp = page.widgets.findIndex(
      (x) => x.userWidgetId === widget.userWidgetId
    );
    if (temp < 0) return false;
    index = temp;
    return true;
  });
  return [pageNumber, index];
};

export const moveWidget = (
  pages: WidgetContextEntity["pages"],
  {
    data: widget,
    target,
  }: GlobalAction<"moveWidget", UserWidget, { target: UserWidget }>
) => {
  const [hoverPage, hoverIndex] = getPageIndex(pages, widget);
  const [targetPage, targetIndex] = getPageIndex(pages, target);
  if ([hoverPage, hoverIndex, targetPage, targetIndex].some((x) => x < 0))
    return pages;
  const temp = [...pages];

  const hoverPageWidgets = [...temp[hoverPage].widgets];
  const targetPageWidgets =
    hoverPage === targetPage ? hoverPageWidgets : [...temp[targetPage].widgets];
  hoverPageWidgets.splice(hoverIndex, 1);
  targetPageWidgets.splice(targetIndex, 0, widget);
  return computePages([
    ...temp.map((page, index) => {
      if (index === hoverPage) return { ...page, widgets: hoverPageWidgets };
      if (index === targetPage) return { ...page, widgets: targetPageWidgets };
      return { ...page, widgets: [...page.widgets] };
    }),
  ]);
};

export const moveWidgetToEmptySpot = (
  { pages, currentPage }: Pick<WidgetContextEntity, "pages" | "currentPage">,
  { data: widget }: GlobalAction<"moveWidgetToEmptySpot", UserWidget>
) => {
  const [originIndex] = getPageIndex(pages, widget);
  if (originIndex < 0) return pages;
  const temp = [...pages];
  temp[originIndex] = {
    ...pages[originIndex],
    widgets: [
      ...pages[originIndex].widgets.filter(
        (w) => w.userWidgetId !== widget.userWidgetId
      ),
    ],
  };
  temp[currentPage] = {
    ...pages[currentPage],
    widgets: [...temp[currentPage].widgets, widget],
  };
  return temp;
};

export const initialWidgets = ({
  devices,
  meetings,
  tickets,
  rooms,
}: {
  devices: boolean;
  meetings: boolean;
  tickets: boolean;
  rooms: boolean;
}) => {
  if (devices) {
    return [
      {
        label: "In a Call",
        userWidgetId: FAKE_WIDGET_ID,
        filters: [
          {
            filterType: "inACall",
            filterValue: "true",
          },
        ],
        widgetType: "Devices",
      },
      {
        label: "Link Up",
        userWidgetId: FAKE_WIDGET_ID,
        filters: [
          {
            filterType: "status",
            payload: ["Up"],
          },
        ],
        widgetType: "Devices",
      },
      {
        label: "Link Error",
        userWidgetId: FAKE_WIDGET_ID,
        filters: [
          {
            filterType: "status",
            payload: ["Unreachable"],
          },
        ],
        widgetType: "Devices",
      },
    ];
  }
  if (tickets) {
    return [
      {
        label: "Critical",
        userWidgetId: FAKE_WIDGET_ID,
        filters: [
          {
            filterType: "priority",
            payload: ["Critical"],
          },
          {
            filterType: "status",
            filterValue: "Open",
          },
        ],
        widgetType: "Tickets",
      },
    ];
  }
  if (rooms) {
    return [
      {
        label: "Online",
        userWidgetId: FAKE_WIDGET_ID,
        filters: [
          {
            filterType: "status",
            payload: ["Online"],
          },
        ],
        widgetType: "Rooms",
      },
    ];
  }
  if (meetings) {
    return [
      {
        label: "Meetings Scheduled Today",
        userWidgetId: FAKE_WIDGET_ID,
        filters: [
          {
            filterType: "time",
            filterValue: "Today",
          },
          {
            filterType: "status",
            payload: ["Scheduled"],
          },
        ],
        widgetType: "Meetings",
      },
    ];
  }
  return [];
};
